import { useCallback, useMemo, useState } from 'react';

import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { Typography } from '@progress/kendo-react-common';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  selectSubscribeFilter,
  setSubscribeFilter,
} from '../../store/slices/save-criteria-modals.slice';
import { toDataTypeResult, useUpdateUserDataRowMutation } from '../../store/api/user-data.api';
import {
  selectSavedFiltersEntries,
  setSavedFiltersEntries,
} from '../../store/slices/saved-filters.slice';
import { UserDataType } from '../../models/user.model';
import { SavedCriteria } from '../../models/saved-filters.model';

export const FREQUENCY_DROPDOWN_UNITS = {
  Off: 'Off',
  Daily: 'Daily',
  Weekly: 'Weekly',
  Monthly: 'Monthly',
};

const DROPDOWN_UNITS = [
  FREQUENCY_DROPDOWN_UNITS.Off,
  FREQUENCY_DROPDOWN_UNITS.Daily,
  FREQUENCY_DROPDOWN_UNITS.Weekly,
  FREQUENCY_DROPDOWN_UNITS.Monthly,
];

/*const FREQUENCY_MAP = {
  [FREQUENCY_DROPDOWN_UNITS.Off]: undefined,
  [FREQUENCY_DROPDOWN_UNITS.Daily]: '0 9 * * *',
  [FREQUENCY_DROPDOWN_UNITS.Weekly]: '0 9 * * MON',
  [FREQUENCY_DROPDOWN_UNITS.Monthly]: '0 9 1 * *',
};*/

export function SubscribeCriteriaModal() {
  const [frequency, setFrequency] = useState(FREQUENCY_DROPDOWN_UNITS.Off);
  const [updateFilter] = useUpdateUserDataRowMutation();

  const currentFilters = useAppSelector(selectSavedFiltersEntries);
  const filterKey = useAppSelector(selectSubscribeFilter);

  const dispatch = useAppDispatch();

  const criteria = useMemo(() => {
    if (filterKey !== undefined && currentFilters) {
      return currentFilters.find((filter) => filter.key === filterKey);
    }

    return;
  }, [currentFilters, filterKey]);

  const closeDialog = useCallback(() => dispatch(setSubscribeFilter()), [dispatch]);

  const onSubscribe = useCallback(async () => {
    if (criteria === undefined) {
      return;
    }

    const isSubscribed = frequency !== FREQUENCY_DROPDOWN_UNITS.Off;
    const subscriptionFrequency = frequency;

    const result = await updateFilter({
      key: criteria.key,
      userDataTypeId: UserDataType.SavedCriteria,
      data: toDataTypeResult<SavedCriteria>({
        ...criteria.value,
        isSubscribed,
        subscriptionFrequency,
      }),
    });

    if ('data' in result && currentFilters) {
      const newFilters = currentFilters.map((filter) => {
        if (filter.key !== filterKey) {
          return filter;
        }

        return {
          ...filter,
          value: {
            ...filter.value,
            isSubscribed,
            subscriptionFrequency,
          },
        };
      });
      dispatch(setSavedFiltersEntries({ entries: newFilters }));
    }
    closeDialog();
  }, [criteria, frequency, updateFilter, currentFilters, closeDialog, dispatch, filterKey]);

  const onChange = (e: DropDownListChangeEvent) => {
    setFrequency((state) => {
      if (DROPDOWN_UNITS.includes(e.value)) {
        return e.value as string;
      }

      return state;
    });
  };

  return (
    <>
      {!!filterKey && (
        <Dialog className="subscribe-criteria-dialog" title="Email Alers" onClose={closeDialog}>
          <div className="dialog-content-vertical" style={{ width: '350px' }}>
            <Typography.p textAlign="center">
              Alert me when there are new results that meet my saved search
            </Typography.p>
            <div className="subscribe-criteria-input">
              <Input
                value={criteria?.value.name ?? 'Saved Criteria'}
                label="Saved Search Name"
                disabled
              />
            </div>
            <div className="subscribe-frequency">
              <DropDownList
                value={frequency}
                data={DROPDOWN_UNITS}
                onChange={onChange}
                fillMode={'outline'}
                label="Frequency"
              />
            </div>
          </div>
          <DialogActionsBar layout="center">
            <Button fillMode="outline" themeColor={'primary'} onClick={closeDialog}>
              Cancel
            </Button>
            <Button themeColor={'primary'} onClick={onSubscribe}>
              Alert Me
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
}
